import { z } from 'zod';
import { savedCompoundCandidateChoice } from './compound-selection.contract.js';
import { singleTaskCorrection, type SingleTaskCorrection } from './task-correction-binding.js';
import { isTaskCorrectionClock } from './task-correction-binding.js';
import { resolveDateTime } from '../broker/date-time-resolver.js';

const id = z.string().min(1).max(200);
export const batchCorrectionBindingSchema = z
  .object({
    type: z.literal('pending_batch_correction_v1'),
    version: z.literal(1),
    planId: id,
    planVersion: z.number().int().nonnegative(),
    draftId: id,
    revisionId: id,
    draftVersion: z.number().int().nonnegative(),
    workingStateVersion: z.number().int().nonnegative(),
  })
  .strict();
export const batchCorrectionRequestSchema = z.object({ sourceMessageId: id }).strict();
export type BatchCorrectionBinding = z.infer<typeof batchCorrectionBindingSchema>;
export type BatchCorrectionOperation =
  | { kind: 'remove' }
  | Extract<SingleTaskCorrection, { kind: 'change' }>;
export interface BatchChoice {
  ordinal: number;
  label: string;
  active: boolean;
}
export type BatchCorrectionParse =
  | { kind: 'edit'; ordinal: number; operation: BatchCorrectionOperation }
  | {
      kind: 'clarify';
      operation?: BatchCorrectionOperation | undefined;
      reason: 'item' | 'field' | 'relation';
    };
const normalize = (value: string) => value.trim().toLowerCase().replaceAll('ё', 'е');
/** The date and ordinal are server-kept; the follow-up supplies only a clock. */
export function completeBatchItemTime(
  text: string,
  date: string,
  timeZone: string | undefined,
  referenceTime: Date,
) {
  const correction = singleTaskCorrection(text);
  if (
    correction?.kind !== 'change' ||
    correction.field !== 'dueAt' ||
    !isTaskCorrectionClock(correction.sourceQuote)
  )
    return undefined;
  const resolved = resolveDateTime({
    rawValue: correction.sourceQuote,
    locale: 'ru',
    timeZone,
    referenceTime,
    fallbackDate: date,
  });
  return resolved.kind === 'exact_datetime' ? resolved.iso : undefined;
}
const ordinalWords = [
  'первой',
  'второй',
  'третьей',
  'четвертой',
  'пятой',
  'шестой',
  'седьмой',
  'восьмой',
  'девятой',
  'десятой',
];
export function batchItemOrdinal(
  text: string,
  choices: readonly BatchChoice[],
): number | undefined {
  const value = normalize(text);
  if (['текущей', 'этой', 'current', 'this'].includes(value))
    return choices.find((row) => row.active)?.ordinal;
  const word = ordinalWords.indexOf(value);
  let ordinal = word < 0 ? undefined : word + 1;
  const number = value.startsWith('№')
    ? value.slice(1)
    : value.endsWith('-й')
      ? value.slice(0, -2)
      : value;
  if (number && [...number].every((char) => char >= '0' && char <= '9')) ordinal = Number(number);
  if (ordinal === undefined) {
    const saved = savedCompoundCandidateChoice(value, []);
    if (saved?.kind === 'ordinal') ordinal = saved.ordinal;
  }
  if (ordinal !== undefined) return choices.find((row) => row.ordinal === ordinal)?.ordinal;
  const labels = choices.filter((row) => normalize(row.label) === value);
  return labels.length === 1 ? labels[0]!.ordinal : undefined;
}
/** Recognize a selection attempt independently of the persisted candidate set. */
export function isBatchOrdinalReply(text: string): boolean {
  const value = normalize(text);
  const number = value.startsWith('№')
    ? value.slice(1)
    : value.endsWith('-й')
      ? value.slice(0, -2)
      : value;
  return (
    ordinalWords.includes(value) ||
    (number.length > 0 && [...number].every((char) => char >= '0' && char <= '9')) ||
    savedCompoundCandidateChoice(value, [])?.kind === 'ordinal'
  );
}
function operation(
  text: string,
): Extract<BatchCorrectionOperation, { kind: 'change' }> | undefined {
  const value = text.trim();
  const lower = normalize(value);
  const quoted = ['название «', 'title «', 'title "', 'название "'].find((prefix) =>
    lower.startsWith(prefix),
  );
  if (quoted) {
    const end = quoted.endsWith('«') ? '»' : '"';
    if (!value.endsWith(end)) return undefined;
    const title = value.slice(quoted.length, -1).trim();
    return title && title.length <= 200
      ? { kind: 'change', field: 'title', value: title, sourceQuote: title }
      : undefined;
  }
  const due = ['срок ', 'due '].find((prefix) => lower.startsWith(prefix));
  const tail = due ? value.slice(due.length).trim() : value;
  const datePrefix =
    due && ['на ', 'в ', 'to ', 'at '].find((prefix) => normalize(tail).startsWith(prefix));
  const parsed = singleTaskCorrection(
    datePrefix ? 'перенеси на ' + tail.slice(datePrefix.length) : tail,
  );
  return parsed?.kind === 'change' ? parsed : undefined;
}
/** Whole selectors plus a single strict 5b field grammar. Never infer a record ID. */
export function parseBatchItemCorrection(
  text: string,
  choices: readonly BatchChoice[],
): BatchCorrectionParse | null {
  const value = text.trim();
  const lower = normalize(value);
  let syntax = lower;
  for (const row of choices) syntax = syntax.replaceAll(normalize(row.label), 'record');
  let outsideQuotes = '';
  let closing = '';
  for (const char of syntax) {
    if (closing) {
      if (char === closing) closing = '';
    } else if (char === '«' || char === '"') closing = char === '«' ? '»' : '"';
    else outsideQuotes += char;
  }
  if ([' и ', ' and ', ';', '\n'].some((join) => outsideQuotes.includes(join))) {
    return [
      'у ',
      'во ',
      'для ',
      'пункт ',
      '№',
      'item ',
      'for ',
      'убери ',
      'исключи ',
      'не надо для ',
      'remove ',
      'exclude ',
      'название',
      'title',
      'перенеси ',
      'move ',
      'на ',
      'в ',
    ].some((prefix) => lower.startsWith(prefix))
      ? { kind: 'clarify', reason: 'field' }
      : null;
  }
  const remove = ['убери ', 'исключи ', 'не надо для ', 'remove ', 'exclude '].find((prefix) =>
    lower.startsWith(prefix),
  );
  if (remove) {
    const ordinal = batchItemOrdinal(value.slice(remove.length), choices);
    return ordinal === undefined
      ? { kind: 'clarify', operation: { kind: 'remove' }, reason: 'item' }
      : { kind: 'edit', ordinal, operation: { kind: 'remove' } };
  }
  if (lower.endsWith(' убери')) {
    const selection = value.slice(0, -' убери'.length).trim();
    if (isBatchOrdinalReply(selection)) {
      const ordinal = batchItemOrdinal(selection, choices);
      return ordinal === undefined
        ? { kind: 'clarify', operation: { kind: 'remove' }, reason: 'item' }
        : { kind: 'edit', ordinal, operation: { kind: 'remove' } };
    }
  }
  const selector = ['во ', 'у ', 'для ', 'пункт ', 'item ', 'for '].find((prefix) =>
    lower.startsWith(prefix),
  );
  if (!selector && !lower.startsWith('№')) {
    const change = operation(value);
    if (!change) return null;
    return {
      kind: 'clarify',
      operation: change.field === 'contact' ? undefined : change,
      reason: change.field === 'contact' ? 'relation' : 'item',
    };
  }
  const rest = selector ? value.slice(selector.length) : value;
  const candidates: Array<{ ordinal: number; operation: BatchCorrectionOperation }> = [];
  let retained: BatchCorrectionOperation | undefined;
  for (let split = 0; split < rest.length; split += 1) {
    if (rest[split] !== ' ') continue;
    let label = rest.slice(0, split);
    if (normalize(label).endsWith(' задаче')) label = label.slice(0, -7);
    const change = operation(rest.slice(split + 1));
    if (!change) continue;
    if (change.field === 'contact') return { kind: 'clarify', reason: 'relation' };
    // Later splits can recognize just the clock inside an already complete dueAt.
    retained ??= change;
    const ordinal = batchItemOrdinal(label, choices);
    if (ordinal !== undefined) candidates.push({ ordinal, operation: change });
  }
  return candidates.length === 1
    ? { kind: 'edit', ...candidates[0]! }
    : { kind: 'clarify', reason: 'item', ...(retained ? { operation: retained } : {}) };
}
export function batchCorrectionForMessage(
  messages: readonly { id: string; role: string; structuredPayload?: unknown }[],
  text: string,
) {
  const last = [...messages].reverse().find((row) => ['assistant', 'user'].includes(row.role));
  return last?.role === 'assistant' &&
    batchCorrectionBindingSchema.safeParse(last.structuredPayload).success &&
    text.trim()
    ? { sourceMessageId: last.id }
    : undefined;
}
